import React, { useState, useEffect } from "react";
import { Search, X, Filter, ChevronLeft, ChevronRight, Check, Loader } from "lucide-react";
import SearchableSelect from "./SearchableSelect";
import { masterService } from "../api/masterService";
import * as inventoryService from "../api/inventoryService";

export default function ProductCatalogModal({
  isOpen,
  onClose,
  onSelect,
  dataSource = "inventory",
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // Data State
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch Categories on Open
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      setCurrentPage(1);
    }
  }, [isOpen]);

  // Fetch products from API with debounce
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        fetchProducts();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, selectedCategory, currentPage, itemsPerPage]);

  const fetchCategories = async () => {
    try {
      const res = await masterService.categories.list({ status: "ACTIVE" });
      setCategories(res.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        query: searchTerm || undefined,
        category_id: selectedCategory || undefined,
        page: currentPage,
        limit: itemsPerPage,
      };

      const response = await inventoryService.getStocks(params);
      const stockList = response.data || [];
      const meta = response.meta || { total: stockList.length, page: currentPage, limit: itemsPerPage };

      // Normalize inventory stock fields to common shape
      const normalized = stockList.map((item) => ({
        ...item,
        stock_id: item.id,
        id: item.product_id || item.id,
        product_id: item.product_id || item.id,
        variant_id: item.variant_id || null,
        name: item.product_name || item.name || "-",
        product_name: item.product_name || item.name || "-",
        variant_name: item.variant_name || null,
        brand_name: item.brand_name || null,
        stock: item.current_stock ?? item.stock ?? 0,
        current_stock: item.current_stock ?? item.stock ?? 0,
        unit_name: item.unit_name || "Pcs",
        category_name: item.category_name || item.category || "-",
        purchase_price: Number(item.purchase_price ?? item.buy_price ?? item.cost_price ?? 0),
        sell_price: Number(item.sell_price ?? item.price ?? 0),
      }));

      setProducts(normalized);
      setTotalItems(meta.total || normalized.length);
    } catch (err) {
      console.error("Error fetching products:", err);
      // Fallback to master product list if inventory stocks endpoint encounters issues
      try {
        const masterRes = await masterService.products.list({
          status: "ACTIVE",
          query: searchTerm || undefined,
          category_id: selectedCategory || undefined,
          page: currentPage,
          limit: itemsPerPage,
        });
        const masterList = masterRes.data || [];
        const normalizedMaster = [];
        masterList.forEach((p) => {
          const variants = p.variants || p.options || [];
          if (Array.isArray(variants) && variants.length > 0) {
            variants.forEach((v) => {
              normalizedMaster.push({
                ...p,
                ...v,
                id: `${p.id}-${v.id}`,
                product_id: p.id,
                variant_id: v.id,
                name: p.name || "-",
                product_name: p.name || "-",
                variant_name: v.brand_name || v.name || null,
                brand_name: v.brand_name || p.brand_name || null,
                stock: v.current_stock ?? v.stock ?? 0,
                current_stock: v.current_stock ?? v.stock ?? 0,
                unit_name: v.unit_name || p.unit?.name || p.unit_name || "Pcs",
                category_name: p.category?.name || p.category_name || "-",
                purchase_price: Number(v.purchase_price ?? v.buy_price ?? p.purchase_price ?? p.buy_price ?? 0),
                sell_price: Number(v.price ?? v.sell_price ?? p.sell_price ?? p.price ?? 0),
              });
            });
          } else {
            normalizedMaster.push({
              ...p,
              id: p.id,
              product_id: p.id,
              variant_id: null,
              name: p.name || "-",
              product_name: p.name || "-",
              variant_name: null,
              brand_name: p.brand?.name || p.brand_name || null,
              stock: p.stock ?? p.current_stock ?? 0,
              current_stock: p.stock ?? p.current_stock ?? 0,
              unit_name: p.unit?.name || p.unit_name || "Pcs",
              category_name: p.category?.name || p.category_name || "-",
              purchase_price: Number(p.purchase_price ?? p.buy_price ?? 0),
              sell_price: Number(p.sell_price ?? p.price ?? 0),
            });
          }
        });
        setProducts(normalizedMaster);
        setTotalItems(masterRes.meta?.total || normalizedMaster.length);
      } catch (fallbackErr) {
        setError("Gagal memuat data produk");
      }
    } finally {
      setLoading(false);
    }
  };

  const getItemKey = (product) => {
    return `${product.product_id || product.id}-${product.variant_id || "base"}`;
  };

  const isProductSelected = (product) => {
    const key = getItemKey(product);
    return selectedItems.some((p) => getItemKey(p) === key);
  };

  const handleToggle = (product) => {
    const key = getItemKey(product);
    if (selectedItems.some((p) => getItemKey(p) === key)) {
      setSelectedItems(selectedItems.filter((p) => getItemKey(p) !== key));
    } else {
      setSelectedItems([...selectedItems, product]);
    }
  };

  const handleSelectAllCurrentPage = () => {
    const allSelected = products.every((p) => isProductSelected(p));
    if (allSelected) {
      // Unselect all on this page
      const pageKeys = new Set(products.map((p) => getItemKey(p)));
      setSelectedItems(selectedItems.filter((p) => !pageKeys.has(getItemKey(p))));
    } else {
      // Add missing ones from this page
      const currentSelectedMap = new Map(selectedItems.map((p) => [getItemKey(p), p]));
      products.forEach((p) => {
        currentSelectedMap.set(getItemKey(p), p);
      });
      setSelectedItems(Array.from(currentSelectedMap.values()));
    }
  };

  const handleSave = () => {
    onSelect(selectedItems);
    onClose();
    setSelectedItems([]);
    setSearchTerm("");
    setSelectedCategory("");
    setCurrentPage(1);
  };

  const handleClose = () => {
    onClose();
    setSelectedItems([]);
    setSearchTerm("");
    setSelectedCategory("");
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startEntry = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, totalItems);
  const isAllPageSelected = products.length > 0 && products.every((p) => isProductSelected(p));

  if (!isOpen) return null;

  return (
    <div
      onClick={handleClose}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl ring-1 ring-slate-200/70 w-full max-w-3xl flex flex-col max-h-[90vh] shadow-2xl overflow-hidden modal-panel-enter">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/70">
          <div>
            <h2 className="text-base font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <span>Katalog Produk</span>
              {selectedItems.length > 0 && (
                <span className="bg-teal-100 text-teal-800 text-xs px-2.5 py-0.5 rounded-full font-semibold">
                  {selectedItems.length} Dipilih
                </span>
              )}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Pilih produk yang ingin ditambahkan ke daftar permintaan pengadaan
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters and Search Bar */}
        <div className="px-6 py-3 border-b border-slate-200 bg-white flex flex-col sm:flex-row gap-2.5">
          {/* Search Box */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Cari nama produk, SKU, barcode, atau varian..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-9 pr-8 py-2 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setCurrentPage(1);
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Category Filter */}
          <div className="sm:w-56">
            <SearchableSelect
              options={[{ id: "", name: "Semua Kategori" }, ...categories]}
              value={selectedCategory}
              onChange={(val) => {
                setSelectedCategory(val || "");
                setCurrentPage(1);
              }}
              placeholder="Semua Kategori"
              displayKey="name"
              valueKey="id"
              searchable={true}
              size="sm"
            />
          </div>
        </div>

        {/* List Content */}
        <div className="flex-1 overflow-auto min-h-[300px]">
          <table className="w-full text-left">
            <thead className="bg-slate-50 sticky top-0 border-b border-slate-200 z-10">
              <tr>
                <th className="px-4 py-3 w-12 text-center">
                  <input
                    type="checkbox"
                    checked={isAllPageSelected}
                    onChange={handleSelectAllCurrentPage}
                    className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500 cursor-pointer"
                    title="Pilih semua di halaman ini"
                  />
                </th>
                <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  Nama Produk / Merek
                </th>
                <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-left hidden sm:table-cell">
                  Kategori
                </th>
                <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center">
                  Stok Saat Ini
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-16 text-center text-slate-500 text-sm">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Loader className="w-6 h-6 animate-spin text-teal-600" />
                      <span>Memuat data produk...</span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-rose-600 text-sm">
                    {error}
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-16 text-center text-slate-400 text-sm">
                    <div className="flex flex-col items-center justify-center gap-1">
                      <p className="font-medium text-slate-600">Tidak ada produk yang cocok</p>
                      <p className="text-xs text-slate-400">Coba ubah kata kunci pencarian atau kategori</p>
                    </div>
                  </td>
                </tr>
              ) : (
                products.map((product) => {
                  const isSelected = isProductSelected(product);
                  const stockNumber = Number(product.current_stock ?? product.stock ?? 0);
                  const isOutOfStock = stockNumber <= 0;

                  return (
                    <tr
                      key={getItemKey(product)}
                      onClick={() => handleToggle(product)}
                      className={`cursor-pointer transition-colors ${
                        isSelected ? "bg-teal-50/50 hover:bg-teal-50" : "hover:bg-slate-50/80"
                      }`}>
                      {/* Checkbox */}
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggle(product)}
                          className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500 cursor-pointer"
                        />
                      </td>

                      {/* Product Name & Details */}
                      <td className="px-4 py-3">
                        <div className="font-semibold text-sm text-slate-900">
                          {product.product_name || product.name}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                          {(product.brand_name || product.variant_name) && (
                            <span className="text-[11px] font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md">
                              Merk: {product.brand_name || product.variant_name}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Category */}
                      <td className="px-4 py-3 text-xs text-slate-600 hidden sm:table-cell">
                        {product.category_name || "-"}
                      </td>

                      {/* Stock Badge */}
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                            isOutOfStock
                              ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200"
                              : stockNumber < 10
                              ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                              : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                          }`}>
                          {stockNumber} {product.unit_name || "Pcs"}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination & Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50/70 flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Pagination Controls */}
          <div className="flex items-center gap-3 text-xs text-slate-600">
            <span>
              Menampilkan {startEntry} - {endEntry} dari {totalItems} produk
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={currentPage <= 1 || loading}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="w-7 h-7 flex items-center justify-center rounded border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 transition">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="px-2 font-semibold text-slate-700">
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                disabled={currentPage >= totalPages || loading}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="w-7 h-7 flex items-center justify-center rounded border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 transition">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-white ring-1 ring-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 font-medium text-xs transition-colors">
              Batal
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={selectedItems.length === 0}
              className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-semibold text-xs transition-colors shadow-sm shadow-teal-600/20 disabled:opacity-50 disabled:cursor-not-allowed">
              {selectedItems.length > 0 ? `Tambahkan (${selectedItems.length}) Produk` : "Pilih Produk"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
